import { FAIconName, useToastMessage } from '@equip.health/ui';
import {
  ACCEPTED_FILE_EXTENSIONS,
  DOCUMENT_UPLOAD_MESSAGES,
  MAX_FILE_SIZE_IN_MB,
  MAX_FILE_UPLOAD_COUNT,
} from '~/lib/constants/documentCenter.constants';
import { getFileExtension } from '../util/file.util';

interface UseDocumentUploadProps {
  patientId: string;
  onUploadSuccess?: (files: File[], modalErrorMessages: string[]) => void;
}

interface ValidationResult {
  invalidFiles: File[];
  invalidTypeFiles: string[];
  invalidSizeFiles: string[];
}

export const useDocumentUpload = ({
  patientId,
  onUploadSuccess,
}: UseDocumentUploadProps) => {
  const { createToast } = useToastMessage();

  const showErrorToast = (message: string, variant: 'critical' | 'warning') => {
    createToast({
      icon: FAIconName.exclamationTriangleRegular,
      id: `upload-error-${patientId}`,
      maxWidth: '720px',
      text: message,
      variant,
    });
  };

  const validateFiles = (files: File[]): ValidationResult => {
    const invalidFiles: File[] = [];
    const invalidTypeFiles: string[] = [];
    const invalidSizeFiles: string[] = [];

    for (const file of files) {
      const ext = getFileExtension(file.name.toLowerCase());
      const isValidExt = ACCEPTED_FILE_EXTENSIONS.includes(ext);
      const isValidSize = file.size <= MAX_FILE_SIZE_IN_MB * 1024 * 1024;

      if (!isValidExt) {
        invalidFiles.push(file);
        invalidTypeFiles.push(file.name);
      } else if (!isValidSize) {
        invalidFiles.push(file);
        invalidSizeFiles.push(file.name);
      }
    }

    return { invalidFiles, invalidSizeFiles, invalidTypeFiles };
  };

  const handleFiles = (files: File[]) => {
    let trimmedFiles = files;
    const modalErrorMessages: string[] = [];
    if (files.length > MAX_FILE_UPLOAD_COUNT) {
      trimmedFiles = files.slice(0, MAX_FILE_UPLOAD_COUNT);
      modalErrorMessages.push(
        DOCUMENT_UPLOAD_MESSAGES.tooManyFiles(MAX_FILE_UPLOAD_COUNT),
      );
    }
    const { invalidFiles, invalidTypeFiles, invalidSizeFiles } =
      validateFiles(trimmedFiles);
    if (invalidTypeFiles.length) {
      modalErrorMessages.push(
        invalidTypeFiles.length === 1
          ? DOCUMENT_UPLOAD_MESSAGES.unsupportedTypeSingle(invalidTypeFiles[0])
          : DOCUMENT_UPLOAD_MESSAGES.unsupportedTypeMultiple(invalidTypeFiles),
      );
    }

    if (invalidSizeFiles.length) {
      modalErrorMessages.push(
        invalidSizeFiles.length === 1
          ? DOCUMENT_UPLOAD_MESSAGES.tooLargeSingle(invalidSizeFiles[0])
          : DOCUMENT_UPLOAD_MESSAGES.tooLargeMultiple(invalidSizeFiles),
      );
    }

    const validFiles = trimmedFiles.filter((f) => !invalidFiles.includes(f));
    if (validFiles.length === 0 && modalErrorMessages.length) {
      for (const msg of modalErrorMessages) {
        showErrorToast(msg, 'critical');
      }
    }
    onUploadSuccess?.(validFiles, modalErrorMessages);
  };

  let fileInput: HTMLInputElement | null = null;

  const cleanup = () => {
    if (fileInput) {
      fileInput.remove();
      fileInput = null;
    }
  };
  const openFileExplorer = () => {
    if (!fileInput) {
      fileInput = document.createElement('input');
      fileInput.type = 'file';
      fileInput.multiple = true;
      fileInput.style.display = 'none';
      fileInput.dataset.testid = 'upload-patient-document-input';
      fileInput.accept = ACCEPTED_FILE_EXTENSIONS.map((e) => `.${e}`).join(',');

      fileInput.addEventListener('change', (e: Event) => {
        const target = e.target as HTMLInputElement;
        const files = Array.from(target.files || []);
        handleFiles(files);
        cleanup();
      });
      fileInput.addEventListener('blur', () => cleanup());
      document.body.appendChild(fileInput);
    }
    fileInput.value = '';
    fileInput.click();
  };

  return { handleFiles, openFileExplorer };
};
